<template>
  <FeatureFlag name="premium_metrics">
    <div v-if="isLoading" class="widget__body-empty">
      <div class="loading-spinner" />
    </div>
    <div
      v-else-if="
        !metric || !metric.data?.data || metric.data?.data.length === 0 || state === 'disabled'
      "
      class="widget__body-empty"
    >
      <IconInfo />
      <p>No data available</p>
    </div>
    <div v-else-if="state === 'single-zone-disabled'" class="widget__body-empty">
      <IconInfo />
      <p>This widget is disabled for single zone events</p>
    </div>

    <slot :metric="metric" v-else />

    <template #disabled>
      <div class="widget__premium-metrics-disabled">
        <h4>{{ widget.name }}</h4>
        <p>
          This widget is only available to premium users. Upgrade to premium to unlock all features.
        </p>
        <div class="widget__premium-metrics-disabled-controls">
          <Button severity="primary">
            Upgrade to premium
            <Tooltip position="top">
              <template v-slot:text>
                <IconPremium />
              </template>
              <template v-slot:tooltip> Premium Feature </template>
            </Tooltip>
          </Button>
          <Button severity="contrast">Learn more</Button>
        </div>
      </div>
    </template>
  </FeatureFlag>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { Button } from 'primevue';
import FeatureFlag from '@/components/global/FeatureFlag.vue';
import IconInfo from '@/icons/IconInfo.svg?component';
import IconPremium from '@/icons/IconPremium.svg?component';
import Tooltip from '@/components/global/Tooltip.vue';
import { getPremiumMetric } from '@/utils/api';
import { useUserStore } from '@/stores/user';
import { useAnalyticsStore } from '@/stores/analytics';
import { useToastStore } from '@/utils/toast';
import type { WidgetFilter, WidgetObject, WidgetSetting } from '@/types/Widget';

const userStore = useUserStore();
const analyticsStore = useAnalyticsStore();
const toast = useToastStore();

const props = defineProps<{
  widget: WidgetObject;
  area?: WidgetFilter;
  zone?: WidgetSetting;
  state?: string | null;
}>();

const emit = defineEmits<{
  metricLoaded: [metric: any];
}>();

const metric = ref<any>(null);

const isLoading = ref(false);

// Watch for metric changes and emit them
watch(metric, (newMetric) => {
  if (newMetric) {
    emit('metricLoaded', newMetric);
  }
});

onMounted(() => {
  if (!analyticsStore.chosenEvent?.id) return;

  if (!userStore.hasFeature('premium_metrics')) {
    return;
  }

  isLoading.value = true;

  getPremiumMetric(userStore.accessToken, analyticsStore.chosenEvent?.id, props.widget.id)
    .then((resp) => {
      metric.value = resp;
    })
    .catch(() => {
      toast.error({
        status: 500,
        message: 'Error loading premium metric',
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
});
</script>

<style lang="scss" scoped>
.widget__premium-metrics-disabled {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: variables.$vantage-base-sizing-xxxs;
  height: 100%;
  justify-content: center;

  .widget__premium-metrics-disabled-controls {
    @include mixins.flex-center();
    flex-direction: row;
    gap: variables.$vantage-base-sizing-xxxs;
    margin-top: variables.$vantage-base-sizing-xs;
  }

  .p-button {
    @include mixins.flex-center();
    gap: variables.$vantage-base-sizing-xxxs;
  }
}
</style>
